import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePrevious, useUpdateEffect } from "react-use";

import { displayMonetaryValue } from "checkout-experience/components/common/monetary-util";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useIsCreditCardWithSurchargeChex } from "checkout-experience/hooks/use-is-credit-card-with-surcharge-chex";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { TransactionCharge } from "checkout-query-client/hooks/use-checkout-session";
import { useDoesSessionHaveSurcharge } from "checkout-query-client/hooks/use-does-session-have-surcharge";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";
import { PaymentType } from "checkout-query-client/types/form-types";

import {
  ChargeType,
  Money,
  PaymentMethodTypeCode,
  RateMode,
} from "frontend-client";

export interface SurchargeExtended extends TransactionCharge {
  text: string;
  priceDisclaimer: string;
}

export const useSurcharge = (): SurchargeExtended | null => {
  const { t, i18n } = useTranslation();
  const pubSub = usePubSub();
  const checkoutForm = useFormContext();
  const { data: isCreditCardForSurcharge } =
    useIsCreditCardWithSurchargeChex(checkoutForm);

  const surcharge = useFindSurcharge(isCreditCardForSurcharge ?? false);
  const prevSurcharge = usePrevious(surcharge);

  useUpdateEffect(() => {
    if (surcharge && !prevSurcharge) {
      pubSub.publish({
        level: "info",
        namespace: PubSubNamespace.payment,
        message: PubSubMessage.ApplySurcharge,
        payload: {
          amount:
            surcharge.chargeMode === RateMode.PERCENTAGE
              ? `${surcharge.purchaseTransactionFeePercent}%`
              : displayMonetaryValue(
                  surcharge.transactionFeeAmount ?? new Money(),
                  i18n.language
                ),
          type:
            surcharge.chargeMode === RateMode.PERCENTAGE ? "percent" : "flat",
        },
      });
    } else if (!surcharge && prevSurcharge) {
      pubSub.publish({
        level: "info",
        namespace: PubSubNamespace.payment,
        message: PubSubMessage.RemoveSurcharge,
      });
    }
  }, [surcharge, prevSurcharge]);

  return surcharge
    ? {
        ...surcharge,
        text: t("surchargeWithAmount", {
          amount: surcharge.purchaseTransactionFeePercent,
        }),
        priceDisclaimer: t("additionalSurchargeWithAmount", {
          amount: displayMonetaryValue(
            surcharge.transactionFeeAmount ?? new Money(),
            i18n.language
          ),
        }),
      }
    : null;
};

export const useFindSurcharge = (
  surchargeEnabled: boolean
): TransactionCharge | undefined => {
  const [surcharge, setSurcharge] = useState<TransactionCharge>();
  const { checkoutSession } = useChexContext();
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { paymentType } = paymentOption ?? {};
  const { transactionCharges } = checkoutSession ?? {};

  useEffect(() => {
    if (surchargeEnabled && paymentType === PaymentType.creditCard) {
      const surchargeObj = transactionCharges?.find(
        (charge) =>
          charge.chargeType === ChargeType.SURCHARGE &&
          charge.paymentMethodTypeCode === PaymentMethodTypeCode.CREDIT_CARD
      ) as TransactionCharge;

      if (surchargeObj) {
        setSurcharge(surchargeObj);
      }
    } else {
      setSurcharge(undefined);
    }
  }, [transactionCharges, surchargeEnabled, paymentType]);

  return surcharge;
};

export const useSurchargeText = (): string | undefined => {
  const { t, i18n } = useTranslation();
  const { checkoutSession, target, merchantSettings } = useChexContext();
  const doesSessionHaveSurcharge = useDoesSessionHaveSurcharge({
    checkoutSession,
    target,
    merchantSurchargeFeeEnabled: merchantSettings?.surchargeFeeEnabled,
  });

  const surcharge = useFindSurcharge(doesSessionHaveSurcharge);

  if (surcharge) {
    const amtPercent = surcharge.purchaseTransactionFeePercent;
    return (
      t("surchargeWithAmount", {
        amount:
          amtPercent !== 0
            ? `${amtPercent}%`
            : displayMonetaryValue(
                surcharge.transactionFeeAmount ?? new Money(),
                i18n.language
              ),
      }) ?? undefined
    );
  }

  return undefined;
};
